import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrendingUp, BarChart3, Zap, ArrowRight, Calendar, Target,
  ChevronRight, Loader2,
} from 'lucide-react';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEMAND_CONFIG = {
  high: { color: 'text-[#1D6B4F]', bg: 'bg-[#1D6B4F]/10', label: 'High Demand' },
  medium: { color: 'text-[#EF9F27]', bg: 'bg-[#EF9F27]/10', label: 'Moderate' },
  low: { color: 'text-[#E24B4A]', bg: 'bg-[#E24B4A]/10', label: 'Low' },
};

const IMPACT_COLORS = {
  high: 'bg-[#E24B4A]', medium: 'bg-[#EF9F27]', low: 'bg-[#888]',
};

const PRIORITY_CONFIG = {
  high: { color: 'text-[#E24B4A]', label: 'High' },
  medium: { color: 'text-[#EF9F27]', label: 'Med' },
  low: { color: 'text-[#5C5347]', label: 'Low' },
};

const EFFORT_CONFIG = {
  easy: { color: 'text-[#1D6B4F]', label: 'Easy' },
  medium: { color: 'text-[#EF9F27]', label: 'Medium' },
  hard: { color: 'text-[#E24B4A]', label: 'Hard' },
};

export default function MarketIntel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/market-intel`, { withCredentials: true });
        if (res.data?.has_business) setData(res.data);
      } catch (e) {
        if (e.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <AppShell active="home">
        <div className="p-5 text-center pt-20">
          <p className="text-sm text-[#5C5347]">Start monitoring first to see market intelligence.</p>
        </div>
      </AppShell>
    );
  }

  const intel = data.intel || {};
  const demand = DEMAND_CONFIG[intel.demand_level] || DEMAND_CONFIG.medium;
  const competition = DEMAND_CONFIG[intel.competition_density] || DEMAND_CONFIG.medium;

  return (
    <AppShell active="home">
      <div className="p-5 max-w-3xl mx-auto pb-28" data-testid="market-intel-page">
        <h1 className="font-display text-2xl font-medium text-[#3C3226] tracking-tight mb-1">Market Intelligence</h1>
        <p className="text-sm text-[#5C5347] mb-4 leading-relaxed">
          AI-powered insights for {data.profession}s in {data.city}, {data.state}
        </p>

        {/* Overview metrics */}
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="intel-overview">
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="p-3 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] text-center"
          >
            <TrendingUp size={16} className={`mx-auto mb-1.5 ${demand.color}`} />
            <p className="text-[10px] text-[#5C5347] mb-0.5">Demand</p>
            <p className={`text-xs font-bold ${demand.color}`}>{demand.label}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="p-3 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] text-center"
          >
            <BarChart3 size={16} className={`mx-auto mb-1.5 ${competition.color}`} />
            <p className="text-[10px] text-[#5C5347] mb-0.5">Competition</p>
            <p className={`text-xs font-bold ${competition.color}`}>{competition.label}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="p-3 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] text-center"
          >
            <Zap size={16} className="mx-auto mb-1.5 text-[#C67A5C]" />
            <p className="text-[10px] text-[#5C5347] mb-0.5">AI Opportunity</p>
            <p className="text-xs font-bold text-[#C67A5C]">{intel.ai_opportunity_score || 0}/100</p>
          </motion.div>
        </div>

        {/* Market Overview */}
        {intel.market_overview && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="p-4 rounded-none bg-[#1D6B4F]/5 border border-[#1D6B4F]/10 mb-4"
            data-testid="market-overview"
          >
            <p className="text-sm text-[#3C3226] leading-relaxed">{intel.market_overview}</p>
          </motion.div>
        )}

        {/* Trending Services */}
        {intel.trending_services?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="mb-4"
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-2">Trending Services</h3>
            <div className="flex flex-wrap gap-1.5" data-testid="trending-services">
              {intel.trending_services.map((svc, i) => (
                <span key={i} className="px-3 py-1.5 rounded-none bg-[#C67A5C]/8 text-[#C67A5C] text-xs font-medium border border-[#C67A5C]/15">
                  {svc}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Key Insights */}
        {intel.insights?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            className="mb-4"
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-2">Key Insights</h3>
            <div className="space-y-2" data-testid="intel-insights">
              {intel.insights.map((insight, i) => (
                <div key={i} className="p-3.5 rounded-none bg-[#F7F4EF] border border-[#D4C9B8]">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${IMPACT_COLORS[insight.impact] || IMPACT_COLORS.medium}`} />
                    <p className="text-sm font-medium text-[#3C3226]">{insight.title}</p>
                    <span className="ml-auto text-[9px] font-bold uppercase text-[#5C5347]/50">{insight.impact} impact</span>
                  </div>
                  <p className="text-xs text-[#5C5347] leading-relaxed pl-3.5">{insight.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Recommended Actions */}
        {intel.recommended_actions?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
            className="mb-4"
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-2">Recommended Actions</h3>
            <div className="space-y-2" data-testid="recommended-actions">
              {intel.recommended_actions.map((action, i) => {
                const prio = PRIORITY_CONFIG[action.priority] || PRIORITY_CONFIG.medium;
                const effort = EFFORT_CONFIG[action.effort] || EFFORT_CONFIG.medium;
                return (
                  <div key={i} className="p-3.5 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] flex items-center gap-3">
                    <div className="w-7 h-7 rounded-none bg-[#F7F4EF] flex items-center justify-center flex-shrink-0">
                      <Target size={13} className="text-[#1D6B4F]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#3C3226]">{action.action}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[9px] font-bold ${prio.color}`}>{prio.label} priority</span>
                        <span className="text-[9px] text-[#5C5347]/40">|</span>
                        <span className={`text-[9px] font-medium ${effort.color}`}>{effort.label} effort</span>
                      </div>
                    </div>
                    <ChevronRight size={12} className="text-[#5C5347]/30 flex-shrink-0" />
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Seasonal Trends */}
        {intel.seasonal_trends?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="mb-4"
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-2">Seasonal Trends</h3>
            <div className="space-y-2" data-testid="seasonal-trends">
              {intel.seasonal_trends.map((trend, i) => (
                <div key={i} className="p-3 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] flex items-center gap-3">
                  <div className="w-7 h-7 rounded-none bg-[#EF9F27]/10 flex items-center justify-center flex-shrink-0">
                    <Calendar size={13} className="text-[#EF9F27]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-[#3C3226]">{trend.period}</p>
                    <p className="text-[11px] text-[#5C5347]">{trend.trend}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Generated timestamp */}
        <p className="text-[10px] text-[#5C5347]/40 text-center mt-6">
          Generated {new Date(data.generated_at).toLocaleDateString()} — refreshes every 24 hours
        </p>
      </div>
    </AppShell>
  );
}
